import { Checkbox, Modal, Popover, SubmitButton } from "@xola/ui-kit";
import { DemographicIcon } from "common";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { Except } from "type-fest";
import { Experience, FieldType, Guest, LineItemTemplate, Unit } from "types";
import { useResource } from "../../hooks/useResource";
import { SecondaryButton } from "../Common/SecondaryButton";
import { useSeller } from "@/hooks/useAccount";
import { hasFieldValue } from "@/models/Guest";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface RemoveGuestModalProps {
    isOpen: boolean;
    demographic: { templateId: ID; quantity: number };
    experience: Experience;
    onRemove: (guest: Guest) => void;
    onClose: () => void;
}

export const RemoveGuestModal = ({ demographic, experience, isOpen, onRemove, onClose }: RemoveGuestModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent demographic={demographic} experience={experience} onRemove={onRemove} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ demographic, experience, onRemove, onClose }: Except<RemoveGuestModalProps, "isOpen">) => {
    const seller = useSeller();
    const [selectedGuest, setSelectedGuest] = useState<Guest>();
    const isPartnerExperience = experience.seller.id !== seller.id;

    const { data: demographicTemplate } = useResource<LineItemTemplate>(
        ["lineItemTemplates", demographic.templateId],
        null,
        { enabled: !isPartnerExperience },
    );

    const guests = useCreatePurchaseStore(
        (state) =>
            state.getActiveItem().guests?.filter((guest) => guest.unit.id === demographicTemplate?.unit?.id) ?? [],
    );

    const isGuestSelected = (guest: Guest) => {
        return selectedGuest?.id === guest.id;
    };

    const handleRemoveClick = () => {
        onRemove(selectedGuest ?? guests[0]);
    };

    return (
        <>
            <Modal.Header>Remove Guest</Modal.Header>

            <Modal.Body>
                <div className="space-y-2">
                    {guests.map((guest: Guest) => (
                        <GuestQuestionnaireItem
                            key={guest.id}
                            guest={guest}
                            isSelected={isGuestSelected(guest)}
                            onGuestSelect={() => setSelectedGuest(guest)}
                        />
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <fieldset className="flex justify-end space-x-4">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                    <SubmitButton color="danger" disabled={!selectedGuest} onClick={handleRemoveClick}>
                        Remove
                    </SubmitButton>
                </fieldset>
            </Modal.Footer>
        </>
    );
};

interface GuestQuestionnaireItemProps {
    guest: Guest;
    isSelected: boolean;
    onGuestSelect: () => void;
}

const GuestQuestionnaireItem = ({ guest, isSelected, onGuestSelect }: GuestQuestionnaireItemProps) => {
    const { data: demographic } = useResource<Unit>(["units", guest.unit.id]);

    return (
        <div
            data-testid="remove-guest-modal-questionnaire-item"
            className="flex cursor-pointer items-center justify-between rounded border border-gray-lighter p-4"
            onClick={onGuestSelect}
        >
            <div className="flex items-center space-x-3">
                <Checkbox className="flex-shrink-0" checked={isSelected} />
                <div>
                    <div>
                        <DemographicIcon name={demographic?.name ?? "Guest"} /> {demographic?.name}
                    </div>

                    <div className="font-bold">{guest.name}</div>
                </div>
            </div>

            <Popover
                className="!border-black !bg-black"
                maxWidth={350}
                placement="right"
                appendTo={document.body}
                zIndex={40}
                trigger="mouseenter"
                skidding={0}
            >
                <span className="text-sm font-bold">
                    {getAnsweredFieldsCountForGuest(guest)}/{guest.formResponse?.fields.length} Answers
                </span>
                <Popover.Content>
                    <div className="space-y-4 p-3">
                        {guest.formResponse?.fields.map((field) => {
                            return field.value ? (
                                <div className="flex flex-col space-y-1 text-white">
                                    <span className="text-sm font-bold">{field.label}</span>
                                    <span className="text-sm">{field.value}</span>
                                </div>
                            ) : null;
                        })}
                    </div>
                </Popover.Content>
            </Popover>
        </div>
    );
};

export const getAnsweredFieldsCountForGuest = (guest: Guest) => {
    let totalAnswered =
        guest.formResponse?.fields.filter((field) => field.type !== FieldType.MARKETING_CONSENT && hasFieldValue(field))
            .length ?? 0;

    const marketingConsentFields =
        guest.formResponse?.fields?.filter((field) => field.type === FieldType.MARKETING_CONSENT) ?? [];
    for (const field of marketingConsentFields) {
        const consentValue = field.value?.toString().toLowerCase();
        if ((!field.optInByDefault && !isEmpty(consentValue)) || (field.optInByDefault && consentValue === "no")) {
            // user has actively responded to the marketing consent question
            totalAnswered++;
        }
    }

    return totalAnswered;
};
